import axios, { AxiosResponse } from "axios"

import { apiConfig } from "./config"
import { Configuration } from "./swagger/configuration"
import { EsfirusTablePropsPagination } from "./hooks/useHandlerPaginationTable"
import { FileAviso, FormDataAviso } from "@components/FormParte/FormAviso"
import { delay } from "@helpers/utils"
import { APIDataResponse } from "@models/api-models"
import { NoticeDefinitive, NoticeDraft } from "@models/notices"

interface ValidateBorradorParams {
  id?: string
  files?: FileAviso[]
}

interface GetListFilters {
  from: number
  to: number
  company?: string | number
  customer?: string | number
  obra?: string | number
  originEmployee?: string
  targetEmployee?: string
  responsibleEmployee?: string
  status?: string
  type?: string
  companyObra?: string;
  branch?: string;
  pagination: EsfirusTablePropsPagination
  orderBy?: string
  orderDirection?: string
}

interface GetListBorradoresResponseType {
  items: NoticeDraft[];
  total_items: number;
  filters: any;
}

interface GetListDefinitivosResponseType {
  items: NoticeDefinitive[];
  total_items: number;
  filters: any;
}

interface ChangeStatusProps {
  unique_id: string
  new_state: string
  new_employee: number
  comments: string
  send_mail_employee: boolean
  send_alert_employee: boolean
  send_mail_contacts: boolean
}

class AvisosService {
  basePath = ""

  constructor(_apiConfig: Configuration) {
    this.basePath = (_apiConfig.basePath || '') as string
  }

  async getBorradorByUniqueId(id: string | number): Promise<AxiosResponse<APIDataResponse<FormDataAviso>, any>> {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + `/avisos-borradores/${id}`,
        headers: {
          Authorization: accessToken
        },
        method: "get",
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async getByUniqueId(id: string | number): Promise<AxiosResponse<APIDataResponse<FormDataAviso>, any>> {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + `/avisos/${id}`,
        headers: {
          Authorization: accessToken
        },
        method: "get",
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async getListBorradores({ from, to, company, customer, obra, originEmployee, targetEmployee, responsibleEmployee, status, type, pagination, companyObra, branch, orderBy, orderDirection }: GetListFilters): Promise<AxiosResponse<APIDataResponse<GetListBorradoresResponseType>, any>> {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos-borradores",
        headers: {
          Authorization: accessToken
        },
        method: "get",
        params: {
          from,
          to,
          company_id: company === "" ? null : company,
          client_id: customer === "" ? null : customer,
          work_id: obra === "" ? null : obra,
          companyObra: companyObra === '' ? null : companyObra,
          branch: branch === '' ? null : branch,
          origin_employee_id: originEmployee === "" ? null : originEmployee,
          target_employee_id: targetEmployee === "" ? null : targetEmployee,
          responsible_employee_id: responsibleEmployee === "" ? null : responsibleEmployee,
          status: status === "" ? null : status,
          type: type === "" ? null : type,
          limit: pagination.sizePage,
          page: pagination.page,
          orderBy: orderBy === "" ? null : orderBy,
          orderDirection: orderDirection === "" ? null : orderDirection,
        }
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async getList({ from, to, company, customer, obra, originEmployee, targetEmployee, responsibleEmployee, status, type, pagination, companyObra, branch, orderBy, orderDirection }: GetListFilters): Promise<AxiosResponse<APIDataResponse<GetListDefinitivosResponseType>, any>> {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos",
        headers: {
          Authorization: accessToken
        },
        method: "get",
        params: {
          from,
          to,
          company_id: company === "" ? null : company,
          client_id: customer === "" ? null : customer,
          work_id: obra === "" ? null : obra,
          companyObra: companyObra === '' ? null : companyObra,
          branch: branch === '' ? null : branch,
          origin_employee_id: originEmployee === "" ? null : originEmployee,
          target_employee_id: targetEmployee === "" ? null : targetEmployee,
          responsible_employee_id: responsibleEmployee === "" ? null : responsibleEmployee,
          status: status === "" ? null : status,
          type: type === "" ? null : type,
          limit: pagination.sizePage,
          page: pagination.page,
          orderBy: orderBy === "" ? null : orderBy,
          orderDirection: orderDirection === "" ? null : orderDirection,

        }
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async createBorrador(data: FormDataAviso, isLastRetry?: boolean): Promise<AxiosResponse<APIDataResponse<NoticeDefinitive>, any>> {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`

    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos-borradores",
        headers: {
          Authorization: accessToken
        },
        method: "post",
        data: {
          ...data,
          date: data.date.valueOf(),
          time: data.time.valueOf()
        }
      })

      return resp
    } catch (error) {
      if (isLastRetry) {
        apiConfig?.catchError(error)
        throw error
      }

      // Hacemos un reintento después de tres segundos.
      await delay(3000)
      const resp = await this.createBorrador(data, true)
      return resp
    }
  }

  async validateBorradorAviso({ id, files }: ValidateBorradorParams): Promise<AxiosResponse<APIDataResponse<null>, any>> {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos-borradores/transfer",
        headers: {
          Authorization: accessToken
        },
        method: "post",
        data: { id: id, files: files }
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async openClose({ unique_id, open }: { unique_id: string, open: boolean }) {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos/close-open",
        headers: {
          Authorization: accessToken
        },
        method: "post",
        data: { unique_id, open }
      })

      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async changeStatus({
    unique_id,
    new_state,
    new_employee,
    comments,
    send_mail_employee,
    send_alert_employee,
    send_mail_contacts,
  }: ChangeStatusProps) {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos/change-status",
        headers: {
          Authorization: accessToken
        },
        method: "post",
        data: {
          unique_id,
          new_state: Number(new_state),
          new_employee,
          comments,
          send_mail_employee,
          send_alert_employee,
          send_mail_contacts,
        }
      })

      return resp
    } catch (error) {
      apiConfig?.catchError(error)
      throw error
    }
  }

  async modify(data: FormDataAviso) {
    const accessToken = `Bearer ${localStorage.getItem("ef-token") as string}`
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos/" + data.unique_id,
        headers: {
          Authorization: accessToken
        },
        method: "put",
        data: data
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
    }
  }

  async downloadFile(file: FileAviso) {
    try {
      const resp = await axios({
        baseURL: this.basePath + "/avisos/download",
        method: "get",
        responseType: "blob",
        params: {
          document_id: file.document_id,
          unique_id: file.unique_id,
          name: file.name
        }
      })
      return resp
    } catch (error) {
      apiConfig?.catchError(error)
    }
  }
}

export const avisosService = new AvisosService(apiConfig)
